import dayjs from 'dayjs';
import {
  guaranteesClientText,
  GUARANTEES_TENDER_DATE_FORMAT,
} from './constants.ts';
import type {
  TGuaranteesMoneyValue,
} from './types.ts';

const createMoneyFormatter = (currency: string) => {
  try {
    return new Intl.NumberFormat('uk-UA', {
      style: 'currency',
      currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  } catch {
    return new Intl.NumberFormat('uk-UA', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  }
};

export const formatGuaranteesAmount = (value: number, currency = 'UAH'): string => {
  const formatter = createMoneyFormatter(currency);

  try {
    return formatter.format(value);
  } catch {
    return `${value.toFixed(2)} ${currency}`;
  }
};

export const formatGuaranteesDate = (value?: string): string => {
  if (!value || !dayjs(value).isValid()) {
    return guaranteesClientText.emptyValue;
  }

  return dayjs(value).format(GUARANTEES_TENDER_DATE_FORMAT);
};

export const formatGuaranteesMoney = (value: TGuaranteesMoneyValue): string => {
  if (value.amount == null) {
    return guaranteesClientText.emptyValue;
  }

  const formattedAmount = formatGuaranteesAmount(value.amount, value.currency || 'UAH');

  if (value.valueAddedTaxIncluded === true) {
    return `${formattedAmount} (${guaranteesClientText.vatIncludedLabel})`;
  }

  if (value.valueAddedTaxIncluded === false) {
    return `${formattedAmount} (${guaranteesClientText.vatExcludedLabel})`;
  }

  return formattedAmount;
};

export const formatGuaranteesPeriod = (startDate?: string, endDate?: string): string => {
  const formattedStartDate = formatGuaranteesDate(startDate);
  const formattedEndDate = formatGuaranteesDate(endDate);

  if (
    formattedStartDate === guaranteesClientText.emptyValue &&
    formattedEndDate === guaranteesClientText.emptyValue
  ) {
    return guaranteesClientText.emptyValue;
  }

  return `${formattedStartDate} - ${formattedEndDate}`;
};

export const getDisplayValue = (value?: string | number | null): string => {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? String(value) : guaranteesClientText.emptyValue;
  }

  return value?.trim() || guaranteesClientText.emptyValue;
};
